import { useEffect } from "react"
import type { ReactNode } from "react"
import CloseIcon from "@mui/icons-material/Close"
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft"
import ChevronRightIcon from "@mui/icons-material/ChevronRight"

interface Props {
  title?: string
  caption?: ReactNode
  /** Render the artwork at the given contain-fit pixel size. */
  renderArtwork: (width: number, height: number) => ReactNode
  aspectRatio: number
  viewportWidth: number
  viewportHeight: number
  onClose: () => void
  onPrev?: () => void
  onNext?: () => void
}

/**
 * Immersive "cinema" view — the piece fills the viewport on a near-black
 * backdrop, chrome fades back, Esc / arrows navigate. Works in both themes
 * but always dims the room so the art leads.
 */
const ArtworkFullscreen = ({
  title,
  caption,
  renderArtwork,
  aspectRatio,
  viewportWidth,
  viewportHeight,
  onClose,
  onPrev,
  onNext,
}: Props) => {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
      else if (e.key === "ArrowLeft") onPrev?.()
      else if (e.key === "ArrowRight") onNext?.()
    }
    window.addEventListener("keydown", onKey)
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = "hidden"
    return () => {
      window.removeEventListener("keydown", onKey)
      document.body.style.overflow = previousOverflow
    }
  }, [onClose, onPrev, onNext])

  const ar = Number.isFinite(aspectRatio) && aspectRatio > 0 ? aspectRatio : 1
  const availW = Math.max(120, viewportWidth - (viewportWidth >= 768 ? 160 : 32))
  const availH = Math.max(120, viewportHeight - (viewportWidth >= 768 ? 120 : 132))
  let height = availH
  let width = height * ar
  if (width > availW) {
    width = availW
    height = width / ar
  }

  return (
    <div
      className="gallery-cinema"
      role="dialog"
      aria-modal="true"
      aria-label={title ? `${title} — fullscreen` : "Artwork fullscreen"}
      onClick={onClose}
    >
      <button type="button" className="gallery-cinema-close" aria-label="Close fullscreen" onClick={onClose}>
        <CloseIcon sx={{ fontSize: 20 }} />
      </button>

      {onPrev && (
        <button
          type="button"
          className="gallery-cinema-nav gallery-cinema-prev"
          aria-label="Previous piece"
          onClick={(e) => { e.stopPropagation(); onPrev() }}
        >
          <ChevronLeftIcon sx={{ fontSize: 30 }} />
        </button>
      )}
      {onNext && (
        <button
          type="button"
          className="gallery-cinema-nav gallery-cinema-next"
          aria-label="Next piece"
          onClick={(e) => { e.stopPropagation(); onNext() }}
        >
          <ChevronRightIcon sx={{ fontSize: 30 }} />
        </button>
      )}

      <figure className="gallery-cinema-figure m-0" onClick={(e) => e.stopPropagation()}>
        <div style={{ width: Math.floor(width), height: Math.floor(height) }} className="gallery-cinema-art">
          {renderArtwork(Math.floor(width), Math.floor(height))}
        </div>
        {caption && <figcaption className="gallery-cinema-caption">{caption}</figcaption>}
      </figure>
    </div>
  )
}

export default ArtworkFullscreen
